<script setup lang="ts">
import * as THREE from 'three'
import { TrackballControls } from 'three/addons/controls/TrackballControls.js'
import ThreeGlobe from 'three-globe'
import { ref } from 'vue'

const props = defineProps<{
  inView: boolean
}>()

// Define a ref to hold the container element
const threeContainer = ref<HTMLDivElement | null>(null)

function initThreeJS() {
  if (!window)
    return

  fetch('/data/countries.geojson').then(res => res.json()).then((countries) => {
    if (!threeContainer.value)
      return

    const N = 60

    const arcsData = [...Array.from({ length: N }).keys()].map(() => ({
      startLat: (Math.random() - 0.5) * 180,
      startLng: (Math.random() - 0.5) * 360,
      endLat: (Math.random() - 0.5) * 180,
      endLng: (Math.random() - 0.5) * 360,
      color: ['#e9d5ff', '#86efac', '#5eead4', '#fdba74'][Math.round(Math.random() * 3)],
    }))

    const Globe = new ThreeGlobe()
      .atmosphereColor('#22D3EE')
      .hexPolygonsData(countries.features)
      .hexPolygonResolution(3)
      .hexPolygonMargin(0.3)
      .hexPolygonUseDots(true)
      .hexPolygonColor(() => `#ccc`)
      .arcsData(arcsData)
      .arcColor('color')
      .arcDashLength(2)
      .arcDashGap(7)
      .arcStroke(0.5)
      .arcDashInitialGap(() => Math.random() * 5)
      .arcDashAnimateTime(1000)

    // Setup renderer
    const renderer = new THREE.WebGLRenderer()
    renderer.setSize(window.innerWidth, window.innerHeight)
    threeContainer.value.appendChild(renderer.domElement)

    // Setup scene
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0x0A0A0A)
    scene.add(Globe)
    scene.add(new THREE.AmbientLight(0x22D3EE, Math.PI))
    scene.add(new THREE.DirectionalLight(0x22D3EE, 0.6 * Math.PI))

    // Setup camera
    const camera = new THREE.PerspectiveCamera()
    camera.aspect = window.innerWidth / window.innerHeight
    camera.updateProjectionMatrix()
    camera.position.z = 270

    // Add camera controls
    const tbControls = new TrackballControls(camera, renderer.domElement)
    tbControls.minDistance = 101
    tbControls.rotateSpeed = 5
    tbControls.zoomSpeed = 0
    tbControls.noZoom = true;

    // Kick-off renderer
    (function animate() { // IIFE
      // Frame cycle
      Globe.rotation.y -= 0.001
      tbControls.update()
      renderer.render(scene, camera)
      requestAnimationFrame(animate)
    })()
  })
}

watch(() => props.inView, (inView) => {
  if (inView) {
    initThreeJS()
  }
})

if (props.inView) {
  initThreeJS()
}
</script>

<template>
  <div ref="threeContainer" class="three-container h-screen" />
</template>

<style>
.three-container {
  @apply w-full overflow-hidden h-[75vh]
}
</style>
